import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { isString } from './type-functions';
const simpleDateRegEx = /^(\d{1,2})[/.-](\d{1,2})$/;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(quarterOfYear);

export class SDate {
  // static utcNow() {
  //   throw new Error('Method not implemented.');
  // }
  static new(date?: undefined | null | string | Dayjs | Date, format?: string): dayjs.Dayjs {
    let response: dayjs.Dayjs;
    if (date === undefined || date === null) {
      response = dayjs();
    } else if (format) {
      response = dayjs(date, format);
    } else {
      response = dayjs(date);
    }
    //hack to make cross DST stringify work, else it will even shift UTC times
    dayjs.prototype.toJSON = function () {
      return this.format();
    };
    return response;
  }

  static newUtc(date?: undefined | string | Dayjs | Date, format?: string): dayjs.Dayjs {
    //hack to make cross DST stringify work, else it will even shift UTC times
    dayjs.prototype.toJSON = function () {
      return this.format();
    };

    if (date === undefined) {
      return dayjs.utc();
    }
    if (format) {
      return dayjs.utc(date, format);
    } else {
      if (SDate.isDayjs(date)) {
        return (date as Dayjs).clone();
      }
      if (isString(date)) {
        //console.log(`Processing date ${date} as string to UTC`);
        const match = simpleDateRegEx.exec(date);
        if (match && match.length > 2) {
          const month = parseInt(match[1]);
          const day = parseInt(match[2]);
          const response = dayjs
            .utc()
            .month(month - 1)
            .date(day);
          //console.log(`Processing date ${date} as string to UTC`, response);
          return response;
        }
      }
      const response = dayjs.utc(date);
      //console.log(`Processing date ${date} to UTC`, response);
      return response;
    }
  }

  static isDayjs(date: unknown): date is dayjs.Dayjs {
    return dayjs.isDayjs(date);
  }

  static getNormalized(): dayjs.Dayjs {
    const localDate = SDate.new();
    const response = this.tz(localDate.startOf('day'), 'utc').startOf('day');
    return response;
  }

  static getNormalizedDate(): Date {
    const localDate = new Date(Date.now());
    localDate.setHours(0, 0, 0, 0);
    //const response = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()));
    return localDate;
  }
  private static fixUTCDate(date: Date): Date {
    if (date) {
      date.setTime(new Date(new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000).toUTCString()).getTime());
    }
    return date;
  }

  private static fixLocalDate(date: Date): Date {
    if (date) {
      date.setTime(new Date(new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000).toUTCString()).getTime());
    }
    return date;
  }

  static ShiftTZToLocal(date: dayjs.Dayjs | string): Date | undefined {
    if (!date || date === '') return undefined;
    return SDate.fixLocalDate(dayjs(date).toDate());
  }

  static ShiftTZToUTC(date: dayjs.Dayjs | string): Date | undefined {
    if (!date || date === '') return undefined;
    return SDate.fixUTCDate(dayjs(date).toDate());
  }

  static NowLocal(): Dayjs {
    return SDate.new(SDate.ShiftTZToUTC(SDate.newUtc()));
  }
  static Min(items: Dayjs[], max: Dayjs): Dayjs | undefined {
    const response = items.reduce(
      (acc, startTime) => (startTime && startTime.isSameOrBefore(acc) ? startTime : acc),
      max
    );
    if (response.isSame(max)) return undefined;
    return response;
  }
  static Max(items: Dayjs[], min: Dayjs): Dayjs | undefined {
    const response = items.reduce((acc, endTime) => (endTime && endTime.isSameOrAfter(acc) ? endTime : acc), min);
    if (response.isSame(min)) return undefined;
    return response;
  }

  static tz(date: Dayjs, tz: string | undefined, justTimezone?: boolean): Dayjs {
    if (!date || !tz) return date;
    const response = date.tz(tz, justTimezone);
    return response;
  }

  static ShiftToTz(date: Dayjs, tz: string | undefined): Dayjs {
    if (!date || !tz) return SDate.new(this.ShiftTZToLocal(date));
    if (tz?.toUpperCase() === 'UTC') return SDate.new(this.ShiftTZToUTC(date)).utc();
    //true doesn't work on UTC for some reason
    return this.tz(date, tz, true);
  }

  static GetStartOfDay(end: Dayjs): Dayjs {
    return end.startOf('day').utc().add(1, 'hour').startOf('day');
  }
  static GetStartOfWeekUtc(end: Dayjs): Dayjs {
    return end.utc().add(-6, 'days').utc().add(1, 'hour').startOf('day');
  }
  static GetEndOfWeekUtc(end: Dayjs): Dayjs {
    return end.utc().add(6, 'days').utc().add(1, 'hour').startOf('day');
  }

  static GetBeginningOfQuarter(end: Dayjs): Dayjs {
    return end.startOf('quarter').utc().startOf('day');
  }
  static GetEndOfQuarter(end: Dayjs): Dayjs {
    return end.endOf('quarter').utc().startOf('day');
  }
}
